import { CboRecordDto } from '@act/shared/data-transfer-objects';
import { Button, FlexContainer, FormBuilder, ResourceCard } from '@act/storybook';
import { Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import {
  Control,
  FormState,
  UseFormGetValues,
  UseFormSetValue,
} from 'react-hook-form';
import { cboRecordRequests } from 'redux/requests/cboRecords.requests';
import { makeRequest } from 'redux/utils/apiRequest';
import { createEmptyCustomResourceDetails } from 'react-components/forms/cboResourceForm/createEmptyCustomResourceDetails.util';
import { ResourceBrowsePanel } from 'react-components/forms/cboResourceForm/ResourceBrowsePanel';
import {
  mapCboRecordToResourceDetails,
  mapPlacePartialToResourceDetails,
  mapResourceDetailsToResourceCardProps,
} from 'react-components/forms/cboResourceForm/mapToResourceDetails.util';
import type { CboResourceFormValues } from 'react-components/forms/cboResourceForm/cboResourceForm.schema';
import { defaultCboResourceFormValues } from 'react-components/forms/cboResourceForm/cboResourceForm.schema';
import {
  ActionFormResourceDetails,
  ActionFormValues,
} from 'react-components/dialog-forms/components/useNeedActionFormtypes';
import { buildNeedActionResourceFormConfig } from './needActionResourceForm.config';

type ResourceSlotPhase = 'browse' | 'form' | 'summary';

export interface NeedActionResourceSlotProps {
  control: Control<ActionFormValues>;
  formState: FormState<ActionFormValues>;
  setValue: UseFormSetValue<ActionFormValues>;
  getValues: UseFormGetValues<ActionFormValues>;
  resourceDetailsIndex: number;
  details: ActionFormResourceDetails;
  /** Needs metadata issue key for resource card icons. */
  needIssueKey?: string;
  onRemove: () => void;
  disabled?: boolean;
}

/**
 * Single need-action resource: browse with Places search, edit form, or summary card.
 *
 * @param props - RHF bindings, row index, and remove handler.
 * @returns Resource slot UI.
 */
export function NeedActionResourceSlot({
  control,
  formState,
  setValue,
  getValues,
  resourceDetailsIndex,
  details,
  needIssueKey,
  onRemove,
  disabled = false,
}: NeedActionResourceSlotProps) {
  const needIconOptions = needIssueKey ? { needIssueKey } : undefined;
  const [recentRecords, setRecentRecords] = useState<CboRecordDto[]>([]);
  const [phase, setPhase] = useState<ResourceSlotPhase>(() =>
    details.resourceName?.trim() ? 'summary' : 'browse'
  );

  useEffect(() => {
    if (details.resourceName?.trim() && phase === 'browse') {
      setPhase('summary');
    }
  }, [details.resourceName, phase]);

  useEffect(() => {
    void (async () => {
      try {
        const records = await makeRequest(cboRecordRequests.getRecentCboRecords, {
          params: { limit: 3 },
        });
        setRecentRecords(records ?? []);
      } catch {
        setRecentRecords([]);
      }
    })();
  }, []);

  const applyPartial = useCallback(
    (
      partial: Partial<ActionFormResourceDetails>,
      nextPhase: ResourceSlotPhase = 'form'
    ) => {
      const current = getValues(`resourceDetails.${resourceDetailsIndex}`);
      setValue(
        `resourceDetails.${resourceDetailsIndex}`,
        { ...current, ...partial },
        { shouldDirty: true, shouldValidate: true }
      );
      setPhase(nextPhase);
    },
    [getValues, resourceDetailsIndex, setValue]
  );

  const handlePlacesAutofill = useCallback(
    (partial: Partial<CboResourceFormValues>) => {
      if (!partial.name?.trim() && !partial.googlePlaceId?.trim()) {
        return;
      }
      applyPartial(
        {
          ...mapPlacePartialToResourceDetails(partial),
          cboRecordId: partial.cboRecordId ?? undefined,
          isCustomResource: false,
        },
        'summary'
      );
    },
    [applyPartial]
  );

  const handleAddCustomResource = useCallback(() => {
    applyPartial(createEmptyCustomResourceDetails());
  }, [applyPartial]);

  const handleRecentSelect = useCallback(
    (record: CboRecordDto) => {
      applyPartial(
        {
          ...mapCboRecordToResourceDetails(record),
          isCustomResource: false,
        },
        'summary'
      );
    },
    [applyPartial]
  );

  const clearSelection = useCallback(() => {
    const current = getValues(`resourceDetails.${resourceDetailsIndex}`);
    setValue(
      `resourceDetails.${resourceDetailsIndex}`,
      {
        ...current,
        ...mapPlacePartialToResourceDetails({
          ...defaultCboResourceFormValues,
          isCustomResource: false,
        }),
        id: current?.id,
        shouldDelete: false,
      },
      { shouldDirty: true, shouldValidate: true }
    );
    setPhase('browse');
  }, [getValues, resourceDetailsIndex, setValue]);

  const confirmSummary = useCallback(() => {
    const name = getValues(
      `resourceDetails.${resourceDetailsIndex}.resourceName`
    )?.trim();
    if (name) {
      setPhase('summary');
    }
  }, [getValues, resourceDetailsIndex]);

  if (phase === 'summary' && details.resourceName?.trim()) {
    return (
      <FlexContainer direction="column" gap={1} width="100%" overflowAllVisible>
        <ResourceCard
          {...mapResourceDetailsToResourceCardProps(details, needIconOptions)}
          onEdit={disabled ? undefined : () => setPhase('form')}
          onRemove={disabled ? undefined : clearSelection}
        />
      </FlexContainer>
    );
  }

  if (phase === 'browse') {
    return (
      <ResourceBrowsePanel
        recentRecords={recentRecords}
        needIconOptions={needIconOptions}
        onRecentSelect={handleRecentSelect}
        onPlacesAutofill={handlePlacesAutofill}
        onAddCustomResource={handleAddCustomResource}
        onRemove={onRemove}
        removeDisabled={disabled}
      />
    );
  }

  return (
    <FlexContainer direction="column" gap={2} width="100%" overflowAllVisible>
      <Button trackingName="set-phase-button"
        variant="text"
        onClick={() =>
          setPhase(details.resourceName?.trim() ? 'summary' : 'browse')
        }
        disabled={disabled}
      >
        {details.resourceName?.trim() ? 'Back to summary' : 'Back to search'}
      </Button>
      {details.isCustomResource ? (
        <Typography variant="bodyMedium" color="text.secondary">
          Manual resource entry (not saved to the organization directory).
        </Typography>
      ) : null}
      <FormBuilder
        control={control}
        formState={formState}
        config={buildNeedActionResourceFormConfig(
          resourceDetailsIndex,
          !details.isCustomResource
        )}
        disabled={disabled}
      />
      <FlexContainer direction="row" gap={1} height="auto" overflowAllVisible>
        <Button trackingName="confirm-summary-button" variant="contained" onClick={confirmSummary} disabled={disabled}>
          Done
        </Button>
        <Button trackingName="remove-button" variant="text" onClick={onRemove} disabled={disabled}>
          Remove
        </Button>
      </FlexContainer>
    </FlexContainer>
  );
}
